import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import type { TelemetrySample } from '../services/devices';

const ChartHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChartCurrent = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.secondary};
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  color: ${({ theme }) => theme.font.color.tertiary};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-style: italic;
`;

type SeriesPoint = { ts: number; value: number | null };

function buildSeries(samples: TelemetrySample[], pick: (s: TelemetrySample) => number | null): SeriesPoint[] {
  return samples
    .map(s => ({ ts: new Date(s.sampled_at).getTime(), value: pick(s) }))
    .filter(p => p.value !== null && Number.isFinite(p.value as number));
}

function formatTick(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

interface SmallChartProps {
  title: string;
  unit?: string;
  data: SeriesPoint[];
  yDomain?: [number | 'auto', number | 'auto'];
  yTickFormatter?: (v: number) => string;
  current?: string;
  color: string;
  step?: boolean;
}

function SmallChart({ title, unit, data, yDomain, yTickFormatter, current, color, step }: SmallChartProps) {
  const theme = useTheme() as any;

  return (
    <div>
      <ChartHeader>
        <ChartTitle>{title}{unit ? ` (${unit})` : ''}</ChartTitle>
        {current !== undefined && <ChartCurrent>now: {current}</ChartCurrent>}
      </ChartHeader>
      {data.length === 0 ? (
        <ChartEmpty>No samples in this window yet</ChartEmpty>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.border.color.light} />
            <XAxis
              dataKey="ts"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={formatTick}
              stroke={theme.font.color.tertiary}
              tick={{ fontSize: 10 }}
              minTickGap={40}
            />
            <YAxis
              domain={yDomain || ['auto', 'auto']}
              tickFormatter={yTickFormatter}
              stroke={theme.font.color.tertiary}
              tick={{ fontSize: 11 }}
              width={48}
            />
            <Tooltip
              labelFormatter={formatTick}
              formatter={(v: any) => [yTickFormatter ? yTickFormatter(v as number) : v, title]}
              contentStyle={{ fontSize: '0.75rem' }}
            />
            <Line
              type={step ? 'stepAfter' : 'monotone'}
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

interface TelemetryChartProps {
  samples: TelemetrySample[];
}

export function TelemetryChart({ samples }: TelemetryChartProps) {
  const theme = useTheme() as any;

  const batteryPct = useMemo(() => buildSeries(samples, s => s.battery_pct), [samples]);
  const signal = useMemo(() => buildSeries(samples, s => s.signal_dbm), [samples]);

  const latestBatteryPct = batteryPct.length > 0 ? batteryPct[batteryPct.length - 1].value as number : null;
  const latestSignal = signal.length > 0 ? signal[signal.length - 1].value as number : null;

  return (
    <Grid>
      <SmallChart
        title="Battery"
        unit="%"
        data={batteryPct}
        yDomain={[0, 100]}
        yTickFormatter={(v) => `${Math.round(v)}`}
        current={latestBatteryPct !== null ? `${Math.round(latestBatteryPct)}%` : undefined}
        color={theme.color?.green || '#16a34a'}
      />
      <SmallChart
        title="Cell Signal"
        unit="dBm"
        data={signal}
        yDomain={['auto', 'auto']}
        yTickFormatter={(v) => `${Math.round(v)}`}
        current={latestSignal !== null ? `${latestSignal} dBm` : undefined}
        color={theme.accent?.primary || '#2563eb'}
      />
    </Grid>
  );
}

export default TelemetryChart;
